import html2canvas from 'html2canvas';
import type { ChatMessage, VisualizationData } from '../../../../nebula/types';

const CHART_RENDER_TIMEOUT_MS = 10000;

export function collectChartVisualizations(messages: ChatMessage[]): VisualizationData[] {
  const charts: VisualizationData[] = [];

  for (const message of messages) {
    if (!message.contentBlocks) continue;
    for (const block of message.contentBlocks) {
      if (
        block.type === 'visualization' &&
        block.data.config.type !== 'table' &&
        block.data.config.type !== 'summary_card'
      ) {
        charts.push(block.data);
      }
    }
  }

  return charts;
}

export async function waitForChartReady(container: HTMLElement | null): Promise<void> {
  const startedAt = Date.now();

  while (Date.now() - startedAt < CHART_RENDER_TIMEOUT_MS) {
    if (container?.querySelector('[data-pdf-ready]')) return;
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  throw new Error('Chart render timed out');
}

export async function captureChartAsPng(chartElement: HTMLElement): Promise<string> {
  const canvas = await html2canvas(chartElement, {
    allowTaint: true,
    useCORS: true,
    scale: 2,
    backgroundColor: '#ffffff',
    logging: false,
  } as Parameters<typeof html2canvas>[1]);

  return canvas.toDataURL('image/png');
}

export async function captureChartsForPdf(
  chartVisualizations: VisualizationData[],
  getRenderContainer: () => HTMLElement | null,
  setCurrentVisualization: (viz: VisualizationData | null) => void,
  onProgress?: (current: number, total: number) => void
): Promise<string[]> {
  const chartImages: string[] = [];

  for (let index = 0; index < chartVisualizations.length; index += 1) {
    const viz = chartVisualizations[index];
    onProgress?.(index + 1, chartVisualizations.length);

    setCurrentVisualization(null);
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    setCurrentVisualization(viz);
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const renderContainer = getRenderContainer();

    try {
      await waitForChartReady(renderContainer);
    } catch {
      await new Promise((resolve) => setTimeout(resolve, 900));
    }

    const chartElement = renderContainer?.querySelector('[data-pdf-chart]') as HTMLElement | null;
    if (!chartElement) {
      console.error('Admin PDF export: chart container not found for', viz.config.title);
      continue;
    }

    try {
      chartImages.push(await captureChartAsPng(chartElement));
    } catch (error) {
      console.error('Admin PDF export: failed to capture chart', viz.config.title, error);
    }
  }

  setCurrentVisualization(null);
  return chartImages;
}
